import type { CenterProps } from '@hover/blueprint';
import { Box, Center, Image, motion } from '@hover/blueprint';
import type { Dispatch, SetStateAction } from 'react';
import React from 'react';

import {
  getGeneratedImageLabel,
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { usePartnerCobranding } from 'src/modules/homeownerApp/hooks/usePartner';
import { BeforeAfterSwitch } from 'src/modules/pro/components/ImageGrid/ImageGrid';
import { Skeleton } from 'src/shared/components/Skeleton';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { useQueryParam } from 'src/shared/hooks';

import { CarouselBadge } from '../../CarouselBadge';

interface LandingPageImageProps extends CenterProps {
  image: AiDisplayImageFragment;
  isBeforeSelected: boolean;
  isLoaded: boolean;
  isBefore?: boolean;
  isDesktop: boolean;
  setIsBeforeSelected: Dispatch<SetStateAction<boolean>>;
  setIsLoaded: Dispatch<SetStateAction<boolean>>;
}

const LandingPageImage = ({
  image,
  isBeforeSelected,
  setIsBeforeSelected,
  setIsLoaded,
  isLoaded,
  isDesktop,
  isBefore = false,
  children,
  ...props
}: LandingPageImageProps) => {
  const t = useHomeownerTranslate();
  const isSelected = isBefore ? isBeforeSelected : !isBeforeSelected;
  const animate = isDesktop || isSelected ? 'visible' : 'hidden';
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';

  const { trackingData } = useHomeownerAppContext();
  const beforeAfterTrackingData = {
    ...trackingData,
    track_id: 'design-original-toggle',
  };
  const cobranding = usePartnerCobranding();

  // Temporary duplication during intermediate state where both carousel
  // and non-carousel versions are available.
  if (isCarouselEnabled) {
    return (
      <motion.div
        animate={animate}
        initial={animate}
        key={image.id}
        position={
          isBefore && !isDesktop
            ? 'relative'
            : { base: 'absolute', tablet: 'relative' }
        }
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }}
      >
        <Skeleton height="100%" isLoaded={isLoaded}>
          <Center flexDir="column" height="100%" position="relative" {...props}>
            <Image
              alt={`embed-landing-page-image-${image.id}`}
              borderRadius={400}
              draggable={false}
              h="calc(100dvw - 80px)"
              maxH={{
                base: cobranding ? '255px' : '280px',
                tablet: '496px',
              }}
              maxW={{ base: '245px', tablet: '496px' }} // For now, always enforce square images
              objectFit="cover"
              onLoad={() => setIsLoaded(true)}
              src={image?.aiGeneratedImage?.generatedImagePath}
              w={{ base: 'calc(100dvw - 80px)', tablet: '100%' }}
            />
            <Box
              display={{ base: 'none', tablet: 'flex' }}
              left={500}
              position="absolute"
              top={500}
            >
              <CarouselBadge
                label={getGeneratedImageLabel(image?.aiGeneratedImage, t)}
              />
            </Box>
            <Box
              bottom={400}
              display={{ base: 'flex', tablet: 'none' }}
              position="absolute"
            >
              <BeforeAfterSwitch
                afterText="design"
                beforeText="original"
                isBeforeSelected={isBeforeSelected}
                setIsBeforeSelected={setIsBeforeSelected}
                trackingData={beforeAfterTrackingData}
              />
            </Box>
            {children}
          </Center>
        </Skeleton>
      </motion.div>
    );
  }
  return (
    <motion.div
      animate={animate}
      initial={animate}
      key={image.id}
      position={{ base: 'absolute', tablet: 'relative' }}
      px={{ base: 500, tablet: 0 }}
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
      }}
    >
      <Skeleton height="100%" isLoaded={isLoaded}>
        <Center flexDir="column" height="100%" position="relative" {...props}>
          <Image
            alt={`embed-landing-page-image-${image.id}`}
            borderRadius={400}
            draggable={false}
            maxH={{ base: '245px', tablet: '496px' }}
            maxW="100%"
            objectFit="cover"
            onLoad={() => setIsLoaded(true)}
            src={image?.aiGeneratedImage?.generatedImagePath}
          />
          <Box
            display={{ base: 'none', tablet: 'flex' }}
            left={500}
            position="absolute"
            top={500}
          >
            <CarouselBadge
              label={getGeneratedImageLabel(image?.aiGeneratedImage, t)}
            />
          </Box>
          <Box
            alignItems="center"
            bottom={0}
            display={{ base: 'flex', tablet: 'none' }}
            justifyContent="center"
            pb={700}
            position="relative"
            pt={500}
          >
            <Box bottom={950} position="absolute">
              <BeforeAfterSwitch
                afterText="design"
                beforeText="original"
                isBeforeSelected={isBeforeSelected}
                setIsBeforeSelected={setIsBeforeSelected}
                trackingData={beforeAfterTrackingData}
              />
            </Box>
          </Box>
          {children}
        </Center>
      </Skeleton>
    </motion.div>
  );
};

export { LandingPageImage };
